import { useCallback, useRef, useState } from 'react'
import { buildBackendUrl } from '@/lib/backend-url'
import { actions, createList, createTodo, store } from '@/store'

const COMMAND_CENTER_ENDPOINT = buildBackendUrl('/api/ai/command-center')

const normalizeText = (value) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')

const findListByName = (listName) => {
  const normalized = normalizeText(listName)
  if (!normalized) return null
  return (
    store.lists.find((list) => normalizeText(list.name) === normalized) ||
    store.lists.find((list) => normalizeText(list.name).includes(normalized)) ||
    store.lists.find((list) => normalized.includes(normalizeText(list.name))) ||
    null
  )
}

const findTaskByTitle = (taskTitle) => {
  const normalized = normalizeText(taskTitle)
  if (!normalized) return null
  return (
    store.todos.find((todo) => normalizeText(todo.text) === normalized) ||
    store.todos.find((todo) => normalizeText(todo.text).includes(normalized)) ||
    store.todos.find((todo) => normalized.includes(normalizeText(todo.text))) ||
    null
  )
}

const ensureList = (listName) => {
  if (!listName) return null
  const existing = findListByName(listName)
  if (existing) return existing

  const nextList = createList(listName.trim())
  actions.addList(nextList)
  return nextList
}

const createTaskInList = (task, listId) => {
  const todo = createTodo({
    text: task.title,
    listId: listId || store.lists[0]?.id || 'personal',
    priority: task.priority || 'medium',
  })
  actions.addTodo(todo)
  return todo
}

const executePlanAction = (action) => {
  if (action.type === 'create_list') {
    ensureList(action.listName)
    return true
  }

  if (action.type === 'rename_list') {
    const list = findListByName(action.targetListName)
    if (!list) return false
    actions.updateList(list.id, { name: action.newName })
    return true
  }

  if (action.type === 'create_task') {
    const list = ensureList(action.listName)
    createTaskInList(action, list?.id)
    return true
  }

  if (action.type === 'create_tasks_batch') {
    const list = ensureList(action.listName)
    let createdCount = 0
    action.tasks.forEach((task) => {
      if (!task?.title) return
      createTaskInList(task, list?.id)
      createdCount += 1
    })
    return createdCount > 0
  }

  if (action.type === 'complete_task') {
    const todo = findTaskByTitle(action.taskTitle)
    if (!todo) return false
    actions.updateTodo(todo.id, { completed: true })
    return true
  }

  if (action.type === 'reopen_task') {
    const todo = findTaskByTitle(action.taskTitle)
    if (!todo) return false
    actions.updateTodo(todo.id, { completed: false })
    return true
  }

  if (action.type === 'delete_task') {
    const todo = findTaskByTitle(action.taskTitle)
    if (!todo) return false
    actions.deleteTodo(todo.id)
    return true
  }

  if (action.type === 'move_task') {
    const todo = findTaskByTitle(action.taskTitle)
    const list = ensureList(action.targetListName)
    if (!todo || !list) return false
    actions.updateTodo(todo.id, { listId: list.id })
    return true
  }

  return false
}

export function useCommandCenterActions() {
  const [isRunning, setIsRunning] = useState(false)
  const abortControllerRef = useRef(null)

  const runPrompt = useCallback(async (input) => {
    const trimmed = input.trim()
    if (!trimmed) {
      return { ok: false, message: 'Type a command first.' }
    }

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    setIsRunning(true)

    try {
      const response = await fetch(COMMAND_CENTER_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          input: trimmed,
          projectId: localStorage.getItem('zoy_project_id') || 'home',
        }),
      })

      if (!response.ok) {
        throw new Error(`Command center request failed: ${response.status}`)
      }

      const plan = await response.json()
      const executed = Array.isArray(plan.actions)
        ? plan.actions.reduce((count, action) => count + (executePlanAction(action) ? 1 : 0), 0)
        : 0

      if (executed > 0) {
        actions.incrementAIAction()
      }

      return {
        ok: executed > 0,
        message:
          plan.summary ||
          (executed > 0
            ? `Executed ${executed} action${executed === 1 ? '' : 's'}.`
            : 'No supported actions found.'),
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return { ok: false, message: 'Cancelled.' }
      }
      console.error('Command center action error:', error)
      return { ok: false, message: 'Failed to run command.' }
    } finally {
      setIsRunning(false)
    }
  }, [])

  return {
    isRunning,
    runPrompt,
  }
}
